import { useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { getComicsByCategory } from '@/mocks/comics';
import { categories, tags } from '@/mocks/categories';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState<string>('latest');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const allComics = useMemo(() => {
    let result = getComicsByCategory(slug);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (selectedTags.length > 0) {
      result = result.filter((c) => selectedTags.some((t) => c.tags.includes(t)));
    }

    switch (sortBy) {
      case 'popular':
        result = [...result].sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result = [...result].sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        break;
    }

    return result;
  }, [slug, searchQuery, selectedTags, sortBy]);

  const totalPages = Math.ceil(allComics.length / pageSize);
  const paginatedComics = allComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
    setCurrentPage(1);
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 虫虫漫画网`,
    description: `虫虫漫画网${currentCategory.name}分类，提供韩国漫画大全免费在线阅读，韩漫下拉式阅读体验。`,
    url: `https://www.chongchongmanhua.com/category/${slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}韩国漫画`}
        description={`虫虫漫画网${currentCategory.name}分类，提供精选韩国漫画大全免费在线阅读。海量${currentCategory.name}韩漫资源，支持下拉式阅读，每日更新。`}
        keywords={`${currentCategory.name},韩国漫画,韩漫,免费漫画,虫虫漫画网`}
        canonicalUrl={`https://www.chongchongmanhua.com/category/${slug}`}
        schema={categorySchema}
      />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="container-main">
            <Breadcrumb items={breadcrumbItems} />
            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                {searchQuery ? `搜索: "${searchQuery}"` : currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共找到 {allComics.length} 部漫画
                {searchQuery && `（搜索关键词: ${searchQuery}）`}
              </p>
            </div>
          </div>
        </div>

        <div className="container-main py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-foreground-950 mb-3">漫画分类</h3>
                  <div className="space-y-0.5">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          slug === cat.slug
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-foreground-950 mb-3">标签筛选</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                          selectedTags.includes(tag)
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-100 text-foreground-600 hover:bg-secondary-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                  {[
                    { key: 'latest', label: '最新' },
                    { key: 'popular', label: '最热' },
                    { key: 'rating', label: '评分' },
                  ].map((option) => (
                    <button
                      key={option.key}
                      onClick={() => { setSortBy(option.key); setCurrentPage(1); }}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                        sortBy === option.key
                          ? 'bg-background-50 text-foreground-950'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {paginatedComics.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                  </div>
                  <h3 className="text-lg font-medium text-foreground-700">没有找到相关漫画</h3>
                  <p className="text-sm text-foreground-500 mt-1">请尝试更换筛选条件或搜索关键词</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                    {paginatedComics.map((comic) => (
                      <ComicCard key={comic.id} comic={comic} variant="default" />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-10">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-primary-500 text-white'
                              : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}